import type { RouteObject } from 'react-router-dom'

import { BackupScreen } from '../screens/backup/BackupScreen.js'
import { ExportScreen } from '../screens/export/ExportScreen.js'
import { InsightsScreen } from '../screens/insights/InsightsScreen.js'
import { ReminderEditorScreen } from '../screens/reminders/ReminderEditorScreen.js'
import { RemindersScreen } from '../screens/reminders/RemindersScreen.js'
import { ReportScreen } from '../screens/report/ReportScreen.js'
import { HomeScreen } from '../screens/log/HomeScreen.js'
import { LogFlowScreen } from '../screens/log/LogFlowScreen.js'
import { SettingsScreen } from '../screens/SettingsScreen.js'
import { TagEditorScreen } from '../screens/tags/TagEditorScreen.js'
import { TagsScreen } from '../screens/tags/TagsScreen.js'
import { EntryDetailScreen } from '../screens/timeline/EntryDetailScreen.js'
import { TimelineScreen } from '../screens/timeline/TimelineScreen.js'
import { TemplateEditorScreen } from '../screens/templates/TemplateEditorScreen.js'
import { TemplateListScreen } from '../screens/templates/TemplateListScreen.js'
import { TrackEditorScreen } from '../screens/tracks/TrackEditorScreen.js'
import { TrackListScreen } from '../screens/tracks/TrackListScreen.js'
import { AppShell } from './AppShell.js'
import { ErrorBoundary } from './ErrorBoundary.js'
import { NotFound } from './NotFound.js'

/**
 * The route map (T-201).
 *
 * Every path is a real URL, not app state, so each one is bookmarkable and
 * survives a hard refresh — which depends on the SPA fallback in T-603
 * rewriting unknown paths to `index.html` rather than returning a 404.
 *
 * Exported as data rather than JSX so the tests can assert the map itself.
 */
export const routes: RouteObject[] = [
  /*
   * The log flow sits outside the shell, so it has no navigation (T-805).
   *
   * The criterion is that the flow occupies the whole screen, and the reason is
   * not aesthetic: it is one question at a time, and a tab bar under it is four
   * standing offers to abandon the check-in halfway. Hiding the nav with CSS
   * would leave the links focusable and in the tab order, which is the version
   * of this that only looks right.
   *
   * It keeps its own error boundary, since the shell's is no longer above it.
   */
  {
    path: '/log',
    element: (
      <ErrorBoundary>
        <LogFlowScreen />
      </ErrorBoundary>
    ),
  },
  {
    path: '/log/:templateId',
    element: (
      <ErrorBoundary>
        <LogFlowScreen />
      </ErrorBoundary>
    ),
  },
  {
    path: '/',
    element: <AppShell />,
    children: [
      { index: true, element: <HomeScreen /> },
      { path: 'timeline', element: <TimelineScreen /> },
      { path: 'timeline/:id', element: <EntryDetailScreen /> },
      { path: 'tags', element: <TagsScreen /> },
      // `new` before nothing dynamic here, but kept adjacent to its list for
      // the same reason as tracks and reminders: the routes read as a pair.
      { path: 'tags/new', element: <TagEditorScreen /> },
      { path: 'insights', element: <InsightsScreen /> },
      { path: 'tracks', element: <TrackListScreen /> },
      // `new` before `:id` is not required by the router — a literal segment
      // wins over a dynamic one — but the editor still reads the param, and
      // treating "new" as an id would send it looking for a track called new.
      { path: 'tracks/new', element: <TrackEditorScreen /> },
      { path: 'tracks/:id', element: <TrackEditorScreen /> },
      { path: 'templates', element: <TemplateListScreen /> },
      { path: 'templates/new', element: <TemplateEditorScreen /> },
      { path: 'templates/:id', element: <TemplateEditorScreen /> },
      { path: 'export', element: <ExportScreen /> },
      { path: 'backup', element: <BackupScreen /> },
      { path: 'report', element: <ReportScreen /> },
      { path: 'reminders', element: <RemindersScreen /> },
      // `new` before `:id` for the same reason as tracks: the editor reads the
      // param, and treating "new" as an id sends it looking for a reminder
      // called new.
      { path: 'reminders/new', element: <ReminderEditorScreen /> },
      { path: 'reminders/:id', element: <ReminderEditorScreen /> },
      { path: 'settings', element: <SettingsScreen /> },
      { path: '*', element: <NotFound /> },
    ],
  },
]
